// Exact integer polygon tests, for ringing a planet (play/planet.ts). The
// polygons are the loops a constellation's lines close, so they are simple:
// no line crosses another. Positions are integers in sky units, so browser,
// designer and Worker agree to the unit.
import { cross, type Point } from './segments';

/** Twice the signed area (the shoelace sum): positive counter-clockwise in a y-down sky. */
export function polygonArea2(poly: readonly Point[]): number {
  let sum = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum;
}

/** Does p lie on an edge of the polygon, corners included? */
export function pointOnPolygonBoundary(p: Point, poly: readonly Point[]): boolean {
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    if (cross(a, b, p) !== 0) continue;
    if (Math.min(a.x, b.x) <= p.x && p.x <= Math.max(a.x, b.x) && Math.min(a.y, b.y) <= p.y && p.y <= Math.max(a.y, b.y)) return true;
  }
  return false;
}

/**
 * Is p strictly inside the polygon? A point on the boundary is not. Crossing
 * number along the ray to +x, with the half-open rule on each edge's y range
 * so a ray through a corner counts once; the crossing's x is compared to p.x
 * by cross-multiplying, so nothing is divided.
 */
export function pointStrictlyInPolygon(p: Point, poly: readonly Point[]): boolean {
  if (poly.length < 3 || pointOnPolygonBoundary(p, poly)) return false;
  let inside = false;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    if (a.y > p.y === b.y > p.y) continue;
    // The edge crosses the ray's line at x = a.x + (p.y − a.y)(b.x − a.x)/(b.y − a.y); is that right of p?
    const dy = b.y - a.y;
    const lhs = (p.y - a.y) * (b.x - a.x);
    const rhs = (p.x - a.x) * dy;
    if (dy > 0 ? lhs > rhs : lhs < rhs) inside = !inside;
  }
  return inside;
}
